import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { RowActions, RowAction } from '@components/common/RowActions'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import type { PerfProbeTarget, PerfProbeTargetRequest } from '@api/types/performance'
import { useCreateProbe, useDeleteProbe, useRunProbe, useUpdateProbe } from '../hooks/usePerformance'
import { formatDateTime, formatMs, formatPercent, PROBE_OUTCOME_COLOR, PROBE_OUTCOME_LABEL } from './format'

interface ProbesTabProps {
  targets?: PerfProbeTarget[]
  loading: boolean
  /** The probe list failed to load. Shown instead of the "nothing is being watched" warning. */
  error?: boolean
  onRetry: () => void
  hours: number
}

const EMPTY_FORM: PerfProbeTargetRequest = {
  name: '',
  url: '',
  method: 'GET',
  intervalSeconds: 60,
  timeoutMs: 10000,
  expectedStatusCode: 200,
  expectedBodyContains: null,
  degradedAboveMs: 1000,
  enabled: true,
  sendAuthHeader: false,
}

/**
 * The endpoints being watched from outside, and what they have been doing.
 *
 * This is the only part of the dashboard that can detect a host being down, because everything else
 * is the API reporting on itself and a process that is not running reports nothing. An empty probe
 * list therefore means outages are invisible, which is why the empty state pushes hard on adding
 * the two health endpoints.
 */
export function ProbesTab({ targets, loading, error: loadError, onRetry, hours }: ProbesTabProps) {
  const [editing, setEditing] = useState<PerfProbeTarget | null>(null)
  const [form, setForm] = useState<PerfProbeTargetRequest | null>(null)
  const [deleting, setDeleting] = useState<PerfProbeTarget | null>(null)
  const [error, setError] = useState<string | null>(null)

  const createProbe = useCreateProbe()
  const updateProbe = useUpdateProbe()
  const deleteProbe = useDeleteProbe()
  const runProbe = useRunProbe()
  const dialogProps = useMobileDialogProps()
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'))

  const actionsFor = (target: PerfProbeTarget): RowAction[] => [
    // Not confirmed: probes are GET/HEAD only, so running one early changes nothing.
    {
      key: 'run',
      label: 'Run now',
      icon: <PlayArrowIcon fontSize="small" />,
      onClick: () => runProbe.mutate(target.id),
      disabled: runProbe.isPending,
    },
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditIcon fontSize="small" />,
      onClick: () => openEdit(target),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteIcon fontSize="small" />,
      color: 'error',
      onClick: () => setDeleting(target),
    },
  ]

  const openNew = () => {
    setEditing(null)
    setForm({ ...EMPTY_FORM })
    setError(null)
  }

  const openEdit = (target: PerfProbeTarget) => {
    setEditing(target)
    setForm({
      name: target.name,
      url: target.url,
      method: target.method,
      intervalSeconds: target.intervalSeconds,
      timeoutMs: target.timeoutMs,
      expectedStatusCode: target.expectedStatusCode,
      expectedBodyContains: target.expectedBodyContains,
      degradedAboveMs: target.degradedAboveMs,
      enabled: target.enabled,
      sendAuthHeader: target.sendAuthHeader,
    })
    setError(null)
  }

  const save = async () => {
    if (!form) return
    setError(null)

    try {
      if (editing) {
        await updateProbe.mutateAsync({ id: editing.id, body: form })
      } else {
        await createProbe.mutateAsync(form)
      }
      setForm(null)
      setEditing(null)
    } catch (e: unknown) {
      // The API validates the URL and the method as well, and its message says which failed.
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Could not save the probe target.'
      setError(message)
    }
  }

  const set = <K extends keyof PerfProbeTargetRequest>(key: K, value: PerfProbeTargetRequest[K]) =>
    setForm((current) => (current ? { ...current, [key]: value } : current))

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1}
      >
        <Typography variant="body2" color="text.secondary">
          Called from the admin API on a timer. Uptime over the last{' '}
          {hours >= 24 ? `${hours / 24} days` : `${hours} hours`}.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          Add target
        </Button>
      </Stack>

      {loadError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Could not load the probe targets. This does not mean nothing is being watched.
        </Alert>
      )}

      {loading && (
        <Typography variant="body2" color="text.secondary">
          Loading probe targets…
        </Typography>
      )}

      {!loading && !loadError && (targets?.length ?? 0) === 0 && (
        <Alert severity="warning">
          <Typography variant="body2" gutterBottom>
            Nothing is being watched from outside, so an API that stops answering entirely will not appear
            anywhere on this page. Every other tab is the API reporting on itself.
          </Typography>
          <Typography variant="body2">
            Add <code>https://api.hadv.quest/health</code> expecting 200, with the body check set to{' '}
            <code>Healthy</code>. Then add <code>https://admapi.hadv.quest/health</code> expecting
            <strong> 401</strong> and no body check: the admin host requires authentication on every path, and
            a 401 still proves the process is up, listening and routing.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={2}>
        {targets?.map((target) => (
          <Grid item xs={12} md={6} key={target.id}>
            <Paper sx={{ p: { xs: 1.5, sm: 2 }, height: '100%' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                spacing={1}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="subtitle1" fontWeight="bold">
                      {target.name}
                    </Typography>
                    {target.lastOutcome != null && (
                      <Chip
                        label={PROBE_OUTCOME_LABEL[target.lastOutcome]}
                        size="small"
                        color={PROBE_OUTCOME_COLOR[target.lastOutcome]}
                      />
                    )}
                    {!target.enabled && <Chip label="Disabled" size="small" variant="outlined" />}
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ wordBreak: 'break-all', display: 'block' }}
                  >
                    {target.method} {target.url}
                  </Typography>
                </Box>

                <RowActions actions={actionsFor(target)} variant={isPhone ? 'labels' : 'icons'} />
              </Stack>

              <Grid container spacing={1} sx={{ mt: 1 }}>
                <Grid item xs={4}>
                  <Metric
                    label="Uptime"
                    value={target.runCount === 0 ? '—' : formatPercent(target.uptimePercent, 2)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Metric
                    label="Average"
                    value={target.runCount === 0 ? '—' : formatMs(target.avgDurationMs)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Metric label="p95" value={target.runCount === 0 ? '—' : formatMs(target.p95DurationMs)} />
                </Grid>
              </Grid>

              <Sparkline target={target} />
              <RunSummary target={target} />

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {target.lastRunUtc ? `Last run ${formatDateTime(target.lastRunUtc)}` : 'Never run'}
                {' · '}every {target.intervalSeconds}s · slow above {target.degradedAboveMs}ms
              </Typography>

              {target.lastError && (
                <Typography
                  variant="caption"
                  color="error.main"
                  sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}
                >
                  {target.lastError}
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Dialog open={form != null} onClose={() => setForm(null)} maxWidth="sm" fullWidth {...dialogProps}>
        <DialogTitle>{editing ? 'Edit probe target' : 'Add probe target'}</DialogTitle>
        <DialogContent dividers>
          {form && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}

              <TextField
                label="Name"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                fullWidth
                autoFocus
              />

              <TextField
                label="URL"
                value={form.url}
                onChange={(e) => set('url', e.target.value)}
                fullWidth
                placeholder="https://api.hadv.quest/health"
                helperText="Absolute http or https. Redirects are not followed."
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  label="Method"
                  value={form.method}
                  onChange={(e) => set('method', e.target.value)}
                  sx={{ minWidth: { sm: 120 } }}
                  fullWidth={isPhone}
                  // GET and HEAD only. These run unattended on a timer, so a method that can change
                  // something eventually would, repeatedly, and nobody would connect the two.
                  helperText="Read-only"
                >
                  <MenuItem value="GET">GET</MenuItem>
                  <MenuItem value="HEAD">HEAD</MenuItem>
                </TextField>

                <TextField
                  label="Expected status"
                  type="number"
                  value={form.expectedStatusCode}
                  onChange={(e) => set('expectedStatusCode', Number(e.target.value))}
                  fullWidth
                />
              </Stack>

              <TextField
                label="Body must contain"
                value={form.expectedBodyContains ?? ''}
                onChange={(e) => set('expectedBodyContains', e.target.value || null)}
                fullWidth
                placeholder="Healthy"
                helperText="Optional, but worth setting: an endpoint that returns 200 with an error payload otherwise scores as working."
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Interval (seconds)"
                  type="number"
                  value={form.intervalSeconds}
                  onChange={(e) => set('intervalSeconds', Number(e.target.value))}
                  fullWidth
                />
                <TextField
                  label="Timeout (ms)"
                  type="number"
                  value={form.timeoutMs}
                  onChange={(e) => set('timeoutMs', Number(e.target.value))}
                  fullWidth
                />
                <TextField
                  label="Slow above (ms)"
                  type="number"
                  value={form.degradedAboveMs}
                  onChange={(e) => set('degradedAboveMs', Number(e.target.value))}
                  fullWidth
                  helperText="Answered but slower counts as degraded, not down."
                />
              </Stack>

              <FormControlLabel
                control={<Switch checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />}
                label="Enabled"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={form.sendAuthHeader}
                    onChange={(e) => set('sendAuthHeader', e.target.checked)}
                  />
                }
                label="Send the configured admin token"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                Only needed for an endpoint behind authentication, and only works when
                Performance:ProbeAuthToken is set on the admin host.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForm(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={createProbe.isPending || updateProbe.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleting != null}
        title={`Delete ${deleting?.name ?? 'target'}?`}
        message="Its entire availability history goes with it. Disable it instead to stop probing but keep the record."
        confirmLabel="Delete"
        destructive
        loading={deleteProbe.isPending}
        onConfirm={async () => {
          if (deleting) await deleteProbe.mutateAsync(deleting.id)
          setDeleting(null)
        }}
        onClose={() => setDeleting(null)}
      />
    </Stack>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Stack spacing={0}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight="bold">
        {value}
      </Typography>
    </Stack>
  )
}

/**
 * The sparkline in words. The bars carry their detail in hover tooltips, which a phone cannot
 * reach, so the facts that matter (how many failed, when the last one was, why) are written out.
 */
function RunSummary({ target }: { target: PerfProbeTarget }) {
  if (target.recent.length === 0) return null

  const failures = target.recent.filter((r) => r.outcome === 2)
  const slow = target.recent.filter((r) => r.outcome === 1).length
  // Oldest first, so the last failure is the last element.
  const lastFailure = failures[failures.length - 1]

  const parts = [
    `${failures.length} ${failures.length === 1 ? 'failure' : 'failures'}`,
    `${slow} slow`,
    `of ${target.recent.length} recent runs`,
  ]

  return (
    <Typography
      variant="caption"
      color={failures.length > 0 ? 'error.main' : 'text.secondary'}
      sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}
    >
      {parts.join(', ')}
      {lastFailure &&
        `. Last failure ${formatDateTime(lastFailure.runAtUtc)}${lastFailure.error ? `: ${lastFailure.error}` : ''}`}
    </Typography>
  )
}

/**
 * One bar per run, oldest on the left.
 *
 * Deliberately not a chart. The only question this answers is "when did it break", and a row of
 * coloured bars answers it faster than any axis could — a red patch in an otherwise green strip
 * carries the time and the duration of an incident in one look.
 */
function Sparkline({ target }: { target: PerfProbeTarget }) {
  if (target.recent.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        No runs recorded in this window.
      </Typography>
    )
  }

  return (
    <Box sx={{ display: 'flex', gap: '2px', mt: 1.5, height: 28, alignItems: 'flex-end' }}>
      {target.recent.map((run, index) => (
        <Tooltip
          key={index}
          title={`${formatDateTime(run.runAtUtc)} — ${PROBE_OUTCOME_LABEL[run.outcome]}, ${formatMs(run.durationMs)}${run.error ? ` (${run.error})` : ''}`}
          enterTouchDelay={0}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 2,
              // Failures are drawn full height so an outage is never a short bar that scans as fast.
              height:
                run.outcome === 2
                  ? '100%'
                  : `${Math.max(15, Math.min(100, (run.durationMs / Math.max(1, target.degradedAboveMs)) * 60))}%`,
              bgcolor: `${PROBE_OUTCOME_COLOR[run.outcome]}.main`,
              borderRadius: '1px',
            }}
          />
        </Tooltip>
      ))}
    </Box>
  )
}
